import React, { useEffect } from 'react';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { Helmet } from 'react-helmet-async';
import { pageview } from './utils/analytics';
import { siteRoutes } from './routes/routes.config';

function currentLocation() {
  return {
    pathname: window.location.pathname,
    search: window.location.search,
  };
}

export default function App() {
  const [location, setLocation] = React.useState(currentLocation);
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
  const route = siteRoutes.find((candidate) => candidate.path === normalizedPath);
  const pageTitle = route?.title || 'page not found';
  const pageDescription = route?.description || "that page isn't here. you can head back to the homepage.";
  const canonicalPath = route?.canonicalPath || (normalizedPath === '/' ? '' : normalizedPath);
  const pageImage = route?.image || 'https://marcplotkin.com/og-image.jpg';
  const pageImageType = route?.imageType || 'image/jpeg';
  const pageImageAlt = route?.path === '/' ? 'Marc Plotkin portrait photo' : pageTitle;
  const canonicalUrl = `https://marcplotkin.com${canonicalPath}`;

  useEffect(() => {
    const updateLocation = () => setLocation(currentLocation());
    window.addEventListener('popstate', updateLocation);
    return () => window.removeEventListener('popstate', updateLocation);
  }, []);

  useEffect(() => {
    // Track page views whenever the location changes
    try {
      pageview(location.pathname + location.search);
    } catch (error) {
      console.warn('Analytics error:', error);
    }
  }, [location]);

  const schemaOrgData = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Marc Plotkin',
    description: siteRoutes[0]!.description,
    url: 'https://marcplotkin.com',
    image: 'https://marcplotkin.com/og-image.jpg',
    sameAs: [
      'https://www.instagram.com/marcplotkin',
      'https://www.youtube.com/user/marcplotkin',
      'https://www.tiktok.com/@plotkin',
      'https://twitter.com/marcplotkin',
      'https://www.linkedin.com/in/marcplotkin/'
    ],
    jobTitle: ['Music Producer', 'Entrepreneur', 'Professor'],
    affiliation: {
      '@type': 'Organization',
      name: "NYU's Clive Davis Institute"
    },
    hasOccupation: [
      {
        '@type': 'Occupation',
        name: 'Music Producer',
        description: 'Grammy-Shortlisted Producer',
        skills: ['Music Production', 'Songwriting', 'Saxophone', 'Guitar', 'Vocals'],
        occupationLocation: { '@type': 'City', name: 'New York' }
      },
      {
        '@type': 'Occupation',
        name: 'Professor',
        description: 'Professor at NYU\'s Clive Davis Institute',
        skills: ['Music Education', 'Music Industry', 'Music Technology'],
        occupationLocation: { '@type': 'City', name: 'New York' }
      },
      {
        '@type': 'Occupation',
        name: 'Entrepreneur',
        description: 'Founder/CEO of Beast Music A.I.',
        skills: ['Artificial Intelligence', 'Music Technology', 'Business Development'],
        occupationLocation: { '@type': 'City', name: 'New York' }
      }
    ],
    knowsAbout: [
      'Music Production',
      'Songwriting',
      'Saxophone Performance',
      'Guitar Performance',
      'Vocal Performance',
      'Music Education',
      'Music Technology',
      'Artificial Intelligence',
      'Entrepreneurship'
    ],
    performerIn: [
      {
        '@type': 'MusicGroup',
        name: 'Session Musician',
        description: 'Professional session work as saxophonist and guitarist'
      }
    ],
    award: [
      'Grammy Shortlist',
      'Bloomberg BizWeek Top 25 Entrepreneur'
    ]
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content={route ? 'index, follow' : 'noindex, follow'} />

        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={pageImage} />
        <meta property="og:image:secure_url" content={pageImage} />
        <meta property="og:image:type" content={pageImageType} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={pageImageAlt} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />
        <meta name="twitter:image:alt" content={pageImageAlt} />

        <meta name="abstract" content={pageDescription} />
        <meta property="linkedin:title" content={pageTitle} />
        <meta property="linkedin:description" content={pageDescription} />
        <meta property="linkedin:image" content={pageImage} />
        <link rel="image_src" href={pageImage} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(schemaOrgData)}
        </script>
      </Helmet>

      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-white focus:text-black focus:rounded focus:text-sm focus:font-medium">
        Skip to content
      </a>
      <div className="relative min-h-screen overflow-hidden">
        <div className="fixed inset-0 bg-gradient-to-r from-background-dark via-purple-900 to-orange-900 animate-gradient-x" />

        <div className="relative z-10 flex flex-col min-h-screen">
          <Header pathname={location.pathname} />
          <main id="main-content" className="relative z-10 container mx-auto px-4 sm:px-6 flex-1">
            {route ? route.element : (
              <section className="mx-auto max-w-2xl rounded-3xl bg-background-card p-6 text-center font-sans not-italic sm:p-10">
                <h1 className="mb-4 text-3xl font-bold text-white">page not found</h1>
                <p className="mb-6 text-lg">that page isn't here. you can head back to the homepage.</p>
                <a href="/" className="inline-flex min-h-[44px] items-center rounded-full border border-white/20 px-5 py-2 text-white hover:bg-white/10">back to homepage</a>
              </section>
            )}
          </main>
          <Footer className="relative z-0" />
        </div>
      </div>
    </>
  );
}
